import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { CheckCircle2, HeartHandshake, ArrowRight } from "lucide-react";
import { trySubmitAttempt } from "@/lib/api";

const OPTION_ORDER = ["A", "B", "C", "D"];

export default function TryQuestionScreen({ question, stageLabel, kind, progress, onContinue, onAnswered = () => {} }) {
  const [selected, setSelected] = useState(null);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const shownAt = useRef(Date.now());

  const options = question.options || {};

  const submit = async () => {
    if (!selected) return;
    setSubmitting(true);
    try {
      const res = await trySubmitAttempt(question.record_id, selected);
      setResult(res);
      onAnswered({ kind, question, selected, result: res, elapsedMs: Date.now() - shownAt.current });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fade-up max-w-2xl mx-auto" data-testid="try-question-screen">
      <div className="flex items-center justify-between mb-4">
        <span
          className="px-3 py-1 rounded-full bg-sky-100 text-sky-700 text-xs font-semibold uppercase tracking-wider"
          data-testid="try-question-stage-tag"
        >
          {stageLabel}
        </span>
        {progress && (
          <span className="text-xs font-semibold text-slate-500" data-testid="try-question-progress">
            Q {progress.current} / {progress.total}
          </span>
        )}
      </div>

      <div className="rounded-3xl p-7 mit-shadow border border-sky-100 bg-white" data-testid="try-question-card">
        {question.has_diagram && question.diagram_svg && (
          <div className="rounded-2xl border border-sky-100 bg-white p-4 mb-5" data-testid="try-question-diagram-frame">
            <div
              className="[&_svg]:w-full [&_svg]:h-auto [&_svg]:max-h-72 [&_svg]:mx-auto"
              dangerouslySetInnerHTML={{ __html: question.diagram_svg }}
            />
          </div>
        )}

        <p className="font-display text-lg md:text-xl text-slate-800 mb-6 leading-snug" data-testid="try-question-text">
          {question.question_text}
        </p>

        <div className="space-y-3" role="radiogroup" data-testid="try-options-list">
          {OPTION_ORDER.filter((k) => options[k] !== undefined).map((key) => {
            const active = selected === key;
            const revealed = !!result;
            const isCorrectOpt = revealed && key === result.correct_answer;
            const isWrongPick = revealed && active && !result.is_correct;
            let cls = "border-slate-200 bg-white text-slate-700 hover:border-sky-300 hover:bg-sky-50";
            if (active && !revealed) cls = "border-sky-400 bg-sky-50 text-slate-900";
            if (revealed && isCorrectOpt) cls = "border-emerald-400 bg-emerald-50 text-emerald-900";
            else if (revealed && isWrongPick) cls = "border-amber-400 bg-amber-50 text-amber-900";
            return (
              <button
                key={key}
                type="button"
                disabled={!!result}
                onClick={() => setSelected(key)}
                className={`w-full text-left px-5 py-4 rounded-2xl border-2 transition-colors tap-44 text-base font-medium flex gap-3 ${cls}`}
                data-testid={`try-option-btn-${key}`}
                aria-pressed={active}
              >
                <span className="font-bold">{key}.</span> <span>{options[key]}</span>
              </button>
            );
          })}
        </div>

        {!result ? (
          <div className="mt-6 flex justify-end">
            <Button
              onClick={submit}
              disabled={!selected || submitting}
              className="rounded-full bg-sky-500 hover:bg-sky-600 text-white font-semibold px-8 py-5 tap-44"
              data-testid="try-submit-answer-btn"
            >
              {submitting ? "Checking…" : "Submit answer"}
            </Button>
          </div>
        ) : (
          <div
            className={`mt-6 rounded-2xl p-5 border ${result.is_correct ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`}
            data-testid="try-result-card"
          >
            <div className="flex items-center gap-2 mb-2">
              {result.is_correct ? (
                <CheckCircle2 className="w-5 h-5 text-emerald-600" data-testid="try-result-correct-icon" />
              ) : (
                <HeartHandshake className="w-5 h-5 text-amber-600" data-testid="try-result-wrong-icon" />
              )}
              <span
                className={`font-display font-bold ${result.is_correct ? "text-emerald-800" : "text-amber-800"}`}
                data-testid="try-result-headline"
              >
                {result.is_correct ? "That's right!" : "Not quite"}
              </span>
            </div>

            {!result.is_correct && (
              <p className="text-sm text-slate-700 mb-2" data-testid="try-result-correct-answer">
                Correct answer: <strong>{result.correct_answer}</strong>
              </p>
            )}

            {result.explanation && (
              <p className="text-slate-700 text-sm leading-relaxed mb-2" data-testid="try-result-explanation">
                {result.explanation}
              </p>
            )}

            {!result.is_correct && result.diagnostic_text && (
              <div className="mt-3 rounded-xl bg-white/70 border border-amber-200 p-3" data-testid="try-result-diagnostic">
                <div className="text-xs uppercase font-bold text-amber-700 tracking-wider mb-1">What went wrong</div>
                <p className="text-sm text-slate-700">{result.diagnostic_text}</p>
              </div>
            )}

            <div className="mt-5 flex justify-end">
              <Button
                onClick={onContinue}
                className="rounded-full bg-sky-500 hover:bg-sky-600 text-white font-semibold px-8 py-5 tap-44"
                data-testid="try-continue-btn"
              >
                Continue <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
